import React, { memo, useEffect, useState } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { cn } from '../lib/utils';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface ScrollProgressProps {
  /** Color of the progress bar - defaults to primary */
  color?: string;
  /** Height of the progress bar */
  height?: number;
  /** Position: 'top' or 'bottom' */
  position?: 'top' | 'bottom';
  /** Additional class names */
  className?: string;
  /** Whether to show the progress bar */
  show?: boolean;
}

/**
 * ScrollProgress component - Shows reading progress as a thin bar at the top of the page.
 * Accessible and respects reduced motion preferences.
 */
const ScrollProgress = memo(function ScrollProgress({
  color = 'var(--primary)',
  height = 3,
  position = 'top',
  className,
  show = true,
}: ScrollProgressProps) {
  const { scrollYProgress } = useScroll();
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);

  // Smooth spring animation for the progress bar
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Only show after some scrolling
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setIsVisible(latest > 0.01);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  if (!show) return null;

  return (
    <>
      {/* Screen reader announcement */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Page scroll progress indicator
      </div>
      
      <motion.div
        className={cn(
          'fixed left-0 right-0 z-50 origin-left',
          position === 'top' ? 'top-0' : 'bottom-0',
          className
        )}
        style={{
          height: `${height}px`,
          background: color,
          scaleX: prefersReducedMotion ? scrollYProgress : scaleX,
          opacity: isVisible ? 1 : 0,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        role="progressbar"
        aria-label="Page scroll progress"
        aria-valuenow={Math.round(scrollYProgress.get() * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </>
  );
});

ScrollProgress.displayName = 'ScrollProgress';

export default ScrollProgress;
